export type FilterKey =
  | "devPairsCount"
  | "howManyRelatedShouldMatch"
  | "relatedMatchingMode"
  | "motionPercent"
  | "bmotionPercent"
  | "maxMCPercent"
  | "twitterFollowers"
  | "twitterCommunity"
  | "twitterVerified"
  | "twitterFollowing"
  | "twitterHasAccount"
  | "openPrice"
  | "totalPairFees"
  | "migrationPercent"
  | "motionGreaterBMotion"
  | "motionAvgPercent"
  | "bmotionAvgPercent"
  | "maxMCAvgPercent"
  | "protocol";

export type FilterType = "range" | "switch" | "min" | "select" | "title" | "checkbox-list";

export interface FilterConfigItem {
  key?: FilterKey;
  label: string;
  type: FilterType;

  // only for numeric-range filters:
  placeholders?: { from: string; to?: string };
  min?: number;
  max?: number;

  // optional tooltip text to show on hover
  tooltip?: string;

  // future extensibility (dropdowns, selects)
  options?: Array<{ label: string; value: string | number }>;
}

export const filterConfig: FilterConfigItem[] = [
  {
    label: "Main Pair",
    type: "title",
  },
  {
    key: "protocol",
    label: "Protocol",
    type: "checkbox-list",
    options: [
      { label: "Pump V1", value: "Pump V1" },
      { label: "Virtual Curve", value: "Virtual Curve" },
      { label: "Meteora AMM V2", value: "Meteora AMM V2" },
      { label: "LaunchLab", value: "LaunchLab" },
      { label: "Pump AMM", value: "Pump AMM" },
      { label: "Sugar", value: "Sugar" },
      { label: "Raydium CPMM", value: "Raydium CPMM" },
      { label: "Moonshot", value: "Moonshot" },
      { label: "Raydium V4", value: "Raydium V4" },
      { label: "Heaven", value: "Heaven" },
      { label: "Meteora DLMM", value: "Meteora DLMM" },
      { label: "Boop", value: "Boop" },
      { label: "Meteora AMM", value: "Meteora AMM" },
      { label: "Raydium CLMM", value: "Raydium CLMM" },
      { label: "Orca", value: "Orca" },
    ],
  },
  {
    key: "motionAvgPercent",
    label: "Motion Avg %",
    type: "range",
    placeholders: { from: "min %", to: "max %" },
    tooltip: `Average Motion percentage of all related pairs.`,
  },
  {
    key: "bmotionAvgPercent",
    label: "BMotion Avg %",
    type: "range",
    placeholders: { from: "min %", to: "max %" },
    tooltip: `Average BMotion percentage of all related pairs.`,
  },
  {
    key: "maxMCAvgPercent",
    label: "Max Market Cap Avg %",
    type: "range",
    placeholders: { from: "min %", to: "max %" },
    tooltip: `Average Max Market Cap percentage of all related pairs.`,
  },
  {
    key: "twitterHasAccount",
    label: "Has Twitter Account",
    type: "switch",
    tooltip: `Filter pairs that have a Twitter account.`,
  },
  {
    key: "twitterVerified",
    label: "Twitter Verified",
    type: "switch",
    tooltip: `Filter pairs where the Twitter account is verified (has blue checkmark).`,
  },
  {
    key: "twitterFollowers",
    label: "Twitter Admin Followers",
    type: "range",
    placeholders: { from: "min", to: "max" },
    tooltip: `Filter by number of Twitter followers of the admin account.`,
  },
  {
    key: "twitterFollowing",
    label: "Twitter Admin Following",
    type: "range",
    placeholders: { from: "min", to: "max" },
    tooltip: `Filter by number of accounts the admin is following.`,
  },
  {
    key: "twitterCommunity",
    label: "Has Twitter Community",
    type: "switch",
    tooltip: `Filter pairs that have Twitter Community.`,
  },
  {
    label: "Dev Pairs",
    type: "title",
  },
  {
    key: "devPairsCount",
    label: "Count",
    type: "range",
    placeholders: { from: "min", to: "max" },
    tooltip: "Total number of token pairs created by the same developer (for all time).",
  },
  {
    key: "migrationPercent",
    label: "Migrated %",
    type: "range",
    placeholders: { from: "min %", to: "max %" },
    tooltip: `Dev pairs migrated percentage.`,
  },
  {
    label: "Related Pairs",
    type: "title",
  },
  {
    key: "howManyRelatedShouldMatch",
    label: "Match At Least To",
    type: "select",
    placeholders: { from: "1-5" },
    options: [
      { label: "1", value: 1 },
      { label: "2", value: 2 },
      { label: "3", value: 3 },
      { label: "4", value: 4 },
      { label: "5", value: 5 },
    ],
    tooltip: `Select how many related pairs should match to the filters.`,
  },
  {
    key: "relatedMatchingMode",
    label: "Matching Mode",
    type: "select",
    options: [
      { label: "Match to N latest", value: "latest" },
      { label: "Match to N visible", value: "any" },
    ],
    tooltip: `Determines how many related pairs should match to the filters.`,
  },
  {
    key: "motionPercent",
    label: "Motion %",
    type: "range",
    placeholders: { from: "min %", to: "max %" },
    tooltip: `Price movement for the first 5 seconds relative to the opening price.`,
  },
  {
    key: "bmotionPercent",
    label: "BMotion %",
    type: "range",
    placeholders: { from: "min %", to: "max %" },
    tooltip: `Price movement for the first 10 seconds relative to the opening price.`,
  },
  {
    key: "maxMCPercent",
    label: "Max Market Cap %",
    type: "range",
    placeholders: { from: "min %", to: "max %" },
    tooltip: `Percentage of the maximum achieved market cap relative to the opening price.`,
  },
  {
    key: "openPrice",
    label: "Open Price",
    type: "range",
    placeholders: { from: "min $", to: "max $" },
    tooltip: `The opening price.`,
  },
  {
    key: "totalPairFees",
    label: "Total Pair Fees",
    type: "range",
    placeholders: { from: "min", to: "max" },
    tooltip: `Total pair fees paid.`,
  },
  {
    key: "motionGreaterBMotion",
    label: "BMotion > Motion",
    type: "switch",
    tooltip: `Match to pairs where BMotion is greater than Motion.`,
  },
];
